import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CorrectedCallTranscript from '../../components/CorrectedCallTranscript';
import { toast } from 'react-toastify';
import config from '../../config';

function authHeaders() {
  const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

// ---------------------------------------------------------------------------
// Display-only transliteration: Kannada script + Devanagari → Roman
// Applied only to driver messages in the transcript panel.
// Nothing here touches the stored event data, agent_worker.py, or call logic.
// ---------------------------------------------------------------------------
const KANNADA_MAP = {
  // Vowels
  'ಅ':'a','ಆ':'aa','ಇ':'i','ಈ':'ii','ಉ':'u','ಊ':'uu','ಋ':'ru',
  'ಎ':'e','ಏ':'ee','ಐ':'ai','ಒ':'o','ಓ':'oo','ಔ':'au','ಅಂ':'am','ಅಃ':'ah',
  // Consonants
  'ಕ':'k','ಖ':'kh','ಗ':'g','ಘ':'gh','ಙ':'ng',
  'ಚ':'ch','ಛ':'chh','ಜ':'j','ಝ':'jh','ಞ':'nj',
  'ಟ':'t','ಠ':'th','ಡ':'d','ಢ':'dh','ಣ':'n',
  'ತ':'t','ಥ':'th','ದ':'d','ಧ':'dh','ನ':'n',
  'ಪ':'p','ಫ':'ph','ಬ':'b','ಭ':'bh','ಮ':'m',
  'ಯ':'y','ರ':'r','ಲ':'l','ವ':'v','ಶ':'sh','ಷ':'sh','ಸ':'s','ಹ':'h',
  'ಳ':'l','ಱ':'r','ಕ್ಷ':'ksh','ಜ್ಞ':'gny',
  // Vowel signs (matras)
  'ಾ':'aa','ಿ':'i','ೀ':'ii','ು':'u','ೂ':'uu','ೃ':'ru',
  'ೆ':'e','ೇ':'ee','ೈ':'ai','ೊ':'o','ೋ':'oo','ೌ':'au',
  'ಂ':'m','ಃ':'h','್':'',
  // Digits
  '೦':'0','೧':'1','೨':'2','೩':'3','೪':'4','೫':'5','೬':'6','೭':'7','೮':'8','೯':'9',
};

const DEVANAGARI_MAP = {
  // Vowels
  'अ':'a','आ':'aa','इ':'i','ई':'ii','उ':'u','ऊ':'uu','ऋ':'ru',
  'ए':'e','ऐ':'ai','ओ':'o','औ':'au','अं':'am','अः':'ah',
  // Consonants
  'क':'k','ख':'kh','ग':'g','घ':'gh','ङ':'ng',
  'च':'ch','छ':'chh','ज':'j','झ':'jh','ञ':'ny',
  'ट':'t','ठ':'th','ड':'d','ढ':'dh','ण':'n',
  'त':'t','थ':'th','द':'d','ध':'dh','न':'n',
  'प':'p','फ':'ph','ब':'b','भ':'bh','म':'m',
  'य':'y','र':'r','ल':'l','व':'v','श':'sh','ष':'sh','स':'s','ह':'h',
  'ळ':'l','क्ष':'ksh','ज्ञ':'gny',
  // Vowel signs (matras)
  'ा':'aa','ि':'i','ी':'ii','ु':'u','ू':'uu','ृ':'ru',
  'े':'e','ै':'ai','ो':'o','ौ':'au',
  'ं':'n','ः':'h','्':'',
  // Digits
  '०':'0','१':'1','२':'2','३':'3','४':'4','५':'5','६':'6','७':'7','८':'8','९':'9',
};

function isKannada(text) {
  return /[\u0C80-\u0CFF]/.test(text);
}
function isDevanagari(text) {
  return /[\u0900-\u097F]/.test(text);
}

function transliterateWithMap(text, map) {
  // Sort keys longest-first so multi-char sequences match before single chars
  const keys = Object.keys(map).sort((a, b) => b.length - a.length);
  let result = '';
  let i = 0;
  while (i < text.length) {
    let matched = false;
    for (const key of keys) {
      if (text.startsWith(key, i)) {
        result += map[key];
        i += key.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      result += text[i];
      i += 1;
    }
  }
  return result;
}

function romanizeText(text) {
  if (!text) return text;
  // Process word by word to preserve spaces/punctuation
  return text.split(/([\u0C80-\u0CFF]+|[\u0900-\u097F]+)/).map(segment => {
    if (isKannada(segment)) return transliterateWithMap(segment, KANNADA_MAP);
    if (isDevanagari(segment)) return transliterateWithMap(segment, DEVANAGARI_MAP);
    return segment;
  }).join('');
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(Number(ts) * (Number(ts) > 1e12 ? 1 : 1000));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatPrice(price) {
  if (price == null || price === '') return null;
  const n = Number(price);
  if (Number.isNaN(n)) return null;
  return `₹${Number.isInteger(n) ? n : n.toFixed(0)}`;
}

function roleLabel(role) {
  if (role === 'user' || role === 'driver') return 'Driver';
  if (role === 'assistant' || role === 'bot') return 'Bot';
  return role || 'System';
}

function bookingLabel(board) {
  if (board?.booking_id != null) return `Booking #${board.booking_id}`;
  if (board?.draft_id != null) return `Draft #${board.draft_id}`;
  return 'Unknown booking';
}

function boardKey(board) {
  if (board?.booking_id != null) return `b:${board.booking_id}`;
  if (board?.draft_id != null) return `d:${board.draft_id}`;
  return board?.booking_key || '';
}

function outcomeStyle(outcome) {
  switch (outcome) {
    case 'accepted':
      return { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0', label: 'Accepted' };
    case 'rejected':
      return { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'Rejected' };
    case 'deferred':
      return { bg: '#fffbeb', color: '#b45309', border: '#fde68a', label: 'Deferred' };
    case 'wrong_person':
      return { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe', label: 'Wrong person' };
    case 'failed':
      return { bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb', label: 'Failed' };
    case 'cancelled':
      return { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'Cut' };
    case 'no_quote':
      return { bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb', label: 'No quote' };
    default:
      return { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb', label: outcome || 'Pending' };
  }
}

function TranscriptPanel({ roomName, live }) {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const feedRef = useRef(null);
  const lastTsRef = useRef(0);
  const abortRef = useRef(null);

  const loadEvents = useCallback(async (room, { reset } = { reset: false }) => {
    if (!room) return;
    try {
      const after = reset ? '' : lastTsRef.current ? `?after_ts=${lastTsRef.current}` : '';
      const res = await fetch(
        `${config.opsApiBase}/voice/calls/${encodeURIComponent(room)}/events${after}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load events');
      const next = data.events || [];
      if (reset) {
        setEvents(next);
        lastTsRef.current = next.reduce((m, e) => Math.max(m, Number(e.ts) || 0), 0);
      } else if (next.length) {
        setEvents((prev) => {
          const seen = new Set(prev.map((e) => `${e.ts}|${e.role}|${e.text}`));
          const merged = [...prev];
          for (const e of next) {
            const key = `${e.ts}|${e.role}|${e.text}`;
            if (!seen.has(key)) merged.push(e);
          }
          return merged;
        });
        lastTsRef.current = Math.max(
          lastTsRef.current,
          ...next.map((e) => Number(e.ts) || 0)
        );
      }
      setError('');
    } catch (e) {
      setError(e.message || 'Events error');
    }
  }, []);

  useEffect(() => {
    if (!roomName) return undefined;
    lastTsRef.current = 0;
    setEvents([]);
    loadEvents(roomName, { reset: true });

    if (!live) return undefined;

    const controller = new AbortController();
    abortRef.current = controller;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    (async () => {
      try {
        const res = await fetch(
          `${config.opsApiBase}/voice/calls/${encodeURIComponent(roomName)}/stream`,
          {
            headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
            signal: controller.signal,
          }
        );
        if (!res.ok || !res.body) throw new Error('SSE unavailable');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const chunks = buf.split('\n\n');
          buf = chunks.pop() || '';
          for (const chunk of chunks) {
            const line = chunk.split('\n').find((l) => l.startsWith('data: '));
            if (!line) continue;
            try {
              const ev = JSON.parse(line.slice(6));
              setEvents((prev) => {
                const key = `${ev.ts}|${ev.role}|${ev.text}`;
                if (prev.some((p) => `${p.ts}|${p.role}|${p.text}` === key)) return prev;
                return [...prev, ev];
              });
              lastTsRef.current = Math.max(lastTsRef.current, Number(ev.ts) || 0);
            } catch {
              /* ignore */
            }
          }
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        const poll = setInterval(() => {
          if (controller.signal.aborted) {
            clearInterval(poll);
            return;
          }
          loadEvents(roomName, { reset: false });
        }, 1500);
        controller.signal.addEventListener('abort', () => clearInterval(poll));
      }
    })();

    return () => {
      controller.abort();
    };
  }, [roomName, live, loadEvents]);

  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events]);

  return (
    <div
      ref={feedRef}
      style={{
        marginTop: 10,
        maxHeight: 280,
        overflowY: 'auto',
        padding: 12,
        background: '#fafafa',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
      }}
    >
      {error && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{error}</div>}
      {events.length === 0 && (
        <div style={{ color: '#6b7280', fontSize: 13 }}>
          {live ? 'Waiting for conversation turns…' : 'No transcript yet.'}
        </div>
      )}
      {events.map((ev, idx) => {
        const isBot = ['assistant', 'bot'].includes(ev.role);
        const isDriver = ['user', 'driver'].includes(ev.role);
        return (
          <div
            key={`${ev.ts}-${idx}`}
            style={{
              marginBottom: 8,
              display: 'flex',
              justifyContent: isDriver ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: '85%',
                padding: '7px 10px',
                borderRadius: 10,
                background: isBot ? '#ecfdf5' : isDriver ? '#eff6ff' : '#f3f4f6',
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>
                {roleLabel(ev.role)} · {formatTime(ev.ts)}
                {ev.step ? ` · ${ev.step}` : ''}
              </div>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {isDriver ? (romanizeText(ev.text) || '—') : (ev.text || '—')}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CallCard({
  call,
  kind,
  expanded,
  onToggle,
  highlight,
  onCut,
  cutting,
}) {
  const outcome = outcomeStyle(call?.outcome);
  const price = formatPrice(call?.quoted_price);
  const isLive = kind === 'active';
  const isDialing = isLive && call?.status === 'dialing';
  const title = isDialing
    ? 'Connecting call…'
    : [call?.vehicle_id, call?.driver_name || call?.driver_first_name || call?.phone || 'Driver']
        .filter(Boolean)
        .join(' · ') || 'Driver';

  let borderLeftStyle = '4px solid #e5e7eb'; // default
  if (kind === 'active') {
    borderLeftStyle = isDialing ? '4px solid #f59e0b' : '4px solid #3b82f6';
  } else if (kind === 'queued') {
    borderLeftStyle = '4px solid #d1d5db';
  } else if (kind === 'completed') {
    if (call?.outcome === 'accepted') borderLeftStyle = '4px solid #10b981';
    else if (call?.outcome === 'rejected') borderLeftStyle = '4px solid #ef4444';
    else if (call?.outcome === 'deferred') borderLeftStyle = '4px solid #f59e0b';
    else borderLeftStyle = '4px solid #9ca3af';
  }

  return (
    <div
      style={{
        border: `1px solid ${highlight ? '#10b981' : '#e5e7eb'}`,
        borderLeft: borderLeftStyle,
        borderRadius: 10,
        padding: 14,
        background: isLive 
          ? '#f8fafc' 
          : highlight 
            ? '#f0fdf4' 
            : '#fff',
        boxShadow: isLive 
          ? '0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06)' 
          : highlight 
            ? '0 0 0 1px #bbf7d0' 
            : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
        marginBottom: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
            {title}
            {isLive && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, color: isDialing ? '#d97706' : '#2563eb', background: isDialing ? '#fef3c7' : '#eff6ff', padding: '2px 8px', borderRadius: 12 }}>
                <span className="pulse-dot-live" style={{ backgroundColor: isDialing ? '#d97706' : '#3b82f6' }}></span>
                {isDialing ? 'Dialing' : 'Live'}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {isLive && (
              <span style={{ textTransform: 'capitalize', fontWeight: 500, color: '#4b5563' }}>{call?.status || 'ringing'} · </span>
            )}
            {call?.phone || '—'}
            {call?.room_name ? ` · ${call.room_name}` : ''}
          </div>
          {kind === 'completed' && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: outcome.bg,
                  color: outcome.color,
                  border: `1px solid ${outcome.border}`,
                }}
              >
                {outcome.label}
              </span>
              {price && (
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{price}</span>
              )}
            </div>
          )}
          {kind === 'completed' && call?.summary_text && (
            <div style={{ fontSize: 13, color: '#374151', marginTop: 6, lineHeight: 1.4 }}>{call.summary_text}</div>
          )}
          {kind === 'queued' && (
            <div style={{ fontSize: 12, color: '#4b5563', marginTop: 6, fontWeight: 500 }}>
              Position #{call?.position || '—'} in queue
            </div>
          )}

          {/* Audio recording player displayed directly outside of "Show details" */}
          {call?.recording_url && (
            <div style={{ marginTop: 10, maxWidth: 420 }}>
              <div style={{ fontSize: 11, color: '#4b5563', fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                🔊 Call Recording
              </div>
              <audio src={call.recording_url} controls style={{ width: '100%', height: 32, outline: 'none' }} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {kind === 'active' && call?.room_name && (
            <button
              type="button"
              onClick={() => onCut && onCut(call)}
              disabled={cutting}
              style={{
                fontSize: 12,
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid #fecaca',
                background: '#fef2f2',
                color: '#b91c1c',
                cursor: cutting ? 'wait' : 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: 600,
              }}
            >
              {cutting ? 'Cutting…' : 'Cut call'}
            </button>
          )}
          {(kind === 'active' || kind === 'completed') && (call?.room_name || call?.vobiz_transcript) && (
            <button
              type="button"
              onClick={onToggle}
              style={{
                fontSize: 12,
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: expanded ? '#eff6ff' : '#fff',
                color: '#1d4ed8',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontWeight: 500,
                boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
              }}
            >
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {call?.vobiz_transcript && (
            <div style={{ padding: 10, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#4b5563', fontWeight: 600, marginBottom: 4 }}>
                Vobiz AI Transcript
              </div>
              <div style={{ fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', maxHeight: 150, overflowY: 'auto' }}>
                {call.vobiz_transcript}
              </div>
            </div>
          )}

          {isLive && call?.room_name ? (
            <div>
              <div style={{ fontSize: 11, color: '#4b5563', fontWeight: 600, marginBottom: 4 }}>
                Live transcript
              </div>
              <TranscriptPanel roomName={call.room_name} live />
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: '#4b5563', fontWeight: 600, marginBottom: 4 }}>
                Corrected transcript
              </div>
              <CorrectedCallTranscript transcript={call?.conversation_transcript} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VoiceBotLiveLogs() {
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });

  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  const [boards, setBoards] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [expandedRoom, setExpandedRoom] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [cuttingRoom, setCuttingRoom] = useState(null);
  const toastedRoomsRef = useRef(new Set());
  const knownCompletedRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const loadBoards = useCallback(async () => {
    try {
      const res = await fetch(`${config.opsApiBase}/voice/boards/recent?limit=30&date=${selectedDate}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load boards');
      const list = data.boards || [];
      setBoards(list);
      setError('');

      // Toast on newly completed accepts (skip first paint so history doesn't spam)
      if (!firstLoadRef.current) {
        for (const board of list) {
          for (const c of board.completed || []) {
            const room = c.room_name;
            if (!room || knownCompletedRef.current.has(room)) continue;
            knownCompletedRef.current.add(room);
            if (
              c.outcome === 'accepted' &&
              c.quoted_price != null &&
              !toastedRoomsRef.current.has(room)
            ) {
              toastedRoomsRef.current.add(room);
              const label = [
                c.vehicle_id,
                c.driver_name || c.driver_first_name,
                formatPrice(c.quoted_price),
              ]
                .filter(Boolean)
                .join(' · ');
              const book =
                board.booking_id != null
                  ? `Booking #${board.booking_id}`
                  : board.draft_id != null
                    ? `Draft #${board.draft_id}`
                    : 'Booking';
              toast.success(`${book}: ${label || 'Driver'} accepted`, { autoClose: 8000 });
            }
          }
        }
      } else {
        for (const board of list) {
          for (const c of board.completed || []) {
            if (c.room_name) knownCompletedRef.current.add(c.room_name);
          }
        }
        firstLoadRef.current = false;
      }

      setSelectedKey((prev) => {
        if (prev && list.some((b) => boardKey(b) === prev)) return prev;
        const withActive = list.find((b) => b.active);
        return withActive ? boardKey(withActive) : list[0] ? boardKey(list[0]) : null;
      });
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadBoards();
    if (selectedDate === todayStr) {
      const t = setInterval(loadBoards, 4000);
      return () => clearInterval(t);
    }
    return undefined;
  }, [loadBoards, selectedDate, todayStr]);

  const selected = useMemo(
    () => boards.find((b) => boardKey(b) === selectedKey) || null,
    [boards, selectedKey]
  );

  const toggleRoom = (room) => {
    setExpandedRoom((prev) => (prev === room ? null : room));
  };

  const cutCall = async (call) => {
    const room = call?.room_name;
    if (!room || cuttingRoom) return;
    const who = call.driver_name || call.driver_first_name || call.phone || 'this driver';
    if (!window.confirm(`Cut the call to ${who}?`)) return;
    setCuttingRoom(room);
    try {
      const res = await fetch(`${config.opsApiBase}/voice/calls/${encodeURIComponent(room)}/hangup`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not cut the call');
      toast.success('Call cut');
      loadBoards();
    } catch (e) {
      toast.error(e.message || 'Could not cut the call');
    } finally {
      setCuttingRoom(null);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16, minHeight: 520 }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes pulse-live {
          0% { transform: scale(0.9); opacity: 0.6; }
          50% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(0.9); opacity: 0.6; }
        }
        .pulse-dot-live {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
          animation: pulse-live 1.8s infinite ease-in-out;
        }
      `}} />
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>Bookings with AI calls</div>
          <input
            type="date"
            value={selectedDate}
            max={todayStr}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setSelectedKey(null);
              setExpandedRoom(null);
            }}
            style={{
              width: '100%',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 400,
              outline: 'none',
              cursor: 'pointer',
              background: '#f9fafb',
            }}
          />
        </div>
        <div style={{ maxHeight: 560, overflowY: 'auto' }}>
          {loading && <div style={{ padding: 14, color: '#6b7280' }}>Loading…</div>}
          {!loading && boards.length === 0 && (
            <div style={{ padding: 14, color: '#6b7280' }}>No AI voice call activity yet.</div>
          )}
          {boards.map((b) => {
            const key = boardKey(b);
            const active = key === selectedKey;
            const queuedN = (b.queued || []).length;
            const doneN = (b.completed || []).length;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setSelectedKey(key);
                  setExpandedRoom(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '12px 14px',
                  border: 'none',
                  borderBottom: '1px solid #f3f4f6',
                  background: active ? '#f0f9ff' : '#fff',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>{bookingLabel(b)}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                  {b.active ? (
                    <span style={{ color: '#047857' }}>Live</span>
                  ) : (
                    <span>Idle</span>
                  )}
                  {queuedN ? ` · ${queuedN} queued` : ''}
                  {doneN ? ` · ${doneN} done` : ''}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        {error && (
          <div style={{ fontSize: 13, color: '#dc2626', padding: '8px 10px', background: '#fef2f2', borderRadius: 8 }}>
            {error}
          </div>
        )}

        {!selected && !loading && (
          <div style={{ color: '#6b7280', padding: 12 }}>Select a booking to see its call board.</div>
        )}

        {selected && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{bookingLabel(selected)}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Current call, queue, and completed summaries. Conversation opens only when you click.
                </div>
              </div>
              {selected.suggestion && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    fontSize: 13,
                    color: '#065f46',
                    fontWeight: 600,
                  }}
                >
                  Lowest quote:{' '}
                  {[
                    selected.suggestion.vehicle_id,
                    selected.suggestion.driver_name,
                    formatPrice(selected.suggestion.quoted_price),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>

            {/* Now Calling Section */}
            <div style={{ background: '#f0f7ff', border: '1px solid #dbeafe', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)' }}>
              <section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 12, fontSize: 14, color: '#1e3a8a' }}>
                  <span className="pulse-dot-live" style={{ backgroundColor: '#2563eb', width: 10, height: 10 }}></span>
                  Now Calling
                </div>
                {selected.active ? (
                  <CallCard
                    call={selected.active}
                    kind="active"
                    expanded={expandedRoom === selected.active.room_name}
                    onToggle={() => toggleRoom(selected.active.room_name)}
                    onCut={cutCall}
                    cutting={cuttingRoom === selected.active.room_name}
                  />
                ) : (
                  <div
                    style={{
                      padding: 12,
                      border: '1px dashed #bfdbfe',
                      borderRadius: 8,
                      color: '#4b5563',
                      fontSize: 13,
                      background: '#fff',
                    }}
                  >
                    No active call. Next queued driver will dial automatically when available.
                  </div>
                )}
              </section>
            </div>

            {/* Queued Section */}
            <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)' }}>
              <section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 12, fontSize: 14, color: '#78350f' }}>
                  <span style={{ fontSize: 16 }}>⏳</span>
                  Queued ({(selected.queued || []).length})
                </div>
                {(selected.queued || []).length === 0 ? (
                  <div
                    style={{
                      padding: 12,
                      border: '1px dashed #fde68a',
                      borderRadius: 8,
                      color: '#4b5563',
                      fontSize: 13,
                      background: '#fff',
                    }}
                  >
                    No drivers waiting.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(selected.queued || []).map((q, idx) => (
                      <CallCard
                        key={`${q.phone}-${q.enqueued_at || idx}`}
                        call={q}
                        kind="queued"
                        expanded={false}
                        onToggle={() => {}}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* Completed Section */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)' }}>
              <section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 12, fontSize: 14, color: '#065f46' }}>
                  <span style={{ fontSize: 16 }}>✅</span>
                  Completed ({(selected.completed || []).length})
                </div>
                {(selected.completed || []).length === 0 ? (
                  <div
                    style={{
                      padding: 12,
                      border: '1px dashed #cbd5e1',
                      borderRadius: 8,
                      color: '#6b7280',
                      fontSize: 13,
                      background: '#fafafa',
                    }}
                  >
                    No completed calls for this booking yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(selected.completed || []).map((c) => (
                      <CallCard
                        key={c.room_name}
                        call={c}
                        kind="completed"
                        expanded={expandedRoom === c.room_name}
                        onToggle={() => toggleRoom(c.room_name)}
                        highlight={
                          selected.suggestion &&
                          selected.suggestion.room_name === c.room_name
                        }
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
